import { PermissionGuard } from '@/components/PermissionGuard';
import { QuantitySelector } from '@/components/QuantitySelector';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAddSimsToCheckout } from '@/hooks/useCheckoutActions';
import { useDefaultSite } from '@/hooks/useDefaultSite';
import { useOperators } from '@/hooks/useOperator';
import { useCurrentOrganization } from '@/hooks/useOrganization';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, ArrowLeft, ShoppingCart } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as z from 'zod';

const operatorImages: Record<string, string> = {
  Orange: '/images/operators/orange.webp',
  SFR: '/images/operators/sfr.svg',
  Bouygues: '/images/operators/bouygues.png',
};

const commandSimSchema = z.object({
  siteId: z.string({ required_error: 'Centre de facturation requis' }),
  operatorId: z.string({ required_error: 'Opérateur requis' }),
  operatorLabel: z.string(),
  quantity: z
    .number({ required_error: 'Quantité requise' })
    .min(5, 'La quantité minimum est de 5'),
});

type CommandSimForm = z.infer<typeof commandSimSchema>;

export function CommandSimView() {
  const navigate = useNavigate();
  const { tenantSlug } = useTenantSlug();
  const { data: organization } = useCurrentOrganization();
  const { data: operators } = useOperators();
  const { organization: siteOrganization, isLoading: isSitesLoading, isError: isSitesError } = useDefaultSite();

  const { mutate: addSimsToCheckout, isPending } = useAddSimsToCheckout();

  const sites = siteOrganization?.sites ?? [];

  const form = useForm<CommandSimForm>({
    resolver: zodResolver(commandSimSchema),
    defaultValues: {
      siteId: '',
      operatorId: '',
      operatorLabel: '',
      quantity: 5,
    },
    mode: 'onChange',
  });

  const { operatorId, quantity, siteId } = form.watch();

  const handleAddSimsToCheckout = async () => {
    if (!organization || !operators) return;
    const data = form.getValues();

    const operator = operators.find((op) => op.id === data.operatorId);
    if (!operator) return;

    addSimsToCheckout({
      organizationId: organization.id,
      operatorId: data.operatorId,
      operatorLabel: operator.name,
      quantity: data.quantity,
      siteId: data.siteId,
    });
  };

  return (
    <div className="space-y-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="space-y-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/o/${tenantSlug}/sim-stock`)}
          className="text-slate-500 hover:text-slate-700 -ml-2 mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Retour
        </Button>
        <h1 className="text-2xl font-bold text-slate-900">
          Commander des SIM
        </h1>
        <p className="text-sm text-slate-500">
          Commandez un stock de cartes SIM vierges pour votre organisation.
        </p>
      </div>

      {/* Card container */}
      <div className="rounded-2xl bg-white p-6 sm:p-8 shadow-sm space-y-8">
        <Form {...form}>
          <form className="space-y-8">
            {/* Info Message */}
            <div className="flex items-start gap-3 p-4 rounded-lg bg-blue-50 border border-blue-200">
              <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
              <div className="space-y-1 text-sm text-blue-800">
                <p className="font-medium">
                  Ce parcours de commande concerne exclusivement les
                  SIMs physiques et non les e-SIMs.
                </p>
                <p>
                  Commander un stock de SIM vierges permet d'activer
                  très rapidement les prochaines créations de lignes ou
                  de remplacer une carte SIM en cas de perte.
                </p>
                <p>
                  Une carte SIM est facturée 3€ à l'acte et ne peut être
                  utilisée qu'une unique fois.
                </p>
              </div>
            </div>

            {/* Site Selection */}
            <section className="space-y-4">
              <h3 className="text-lg font-bold text-slate-900">
                Centre de facturation
              </h3>

              <FormField
                control={form.control}
                name="siteId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-slate-700">
                      Centre de facturation
                    </FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={isSitesLoading || isSitesError}
                      >
                        <SelectTrigger className="h-10">
                          <SelectValue
                            placeholder={
                              isSitesLoading
                                ? 'Chargement...'
                                : 'Sélectionnez un centre de facturation'
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {sites.length === 0 && (
                            <div className="px-4 py-3 text-sm text-slate-500">
                              Aucun centre de facturation disponible
                            </div>
                          )}
                          {sites.map((site) => (
                            <SelectItem key={site.id} value={site.id}>
                              {site.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </section>

            {/* Network Selection */}
            <section className="space-y-4">
              <h3 className="text-lg font-bold text-slate-900">
                Sélection du réseau
              </h3>
              <label className="block text-sm font-medium text-slate-700 mb-3">
                Opérateur
              </label>

              <FormField
                control={form.control}
                name="operatorId"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="flex gap-3">
                        {operators?.map((op) => (
                          <button
                            key={op.id}
                            type="button"
                            onClick={() => {
                              field.onChange(op.id);
                              form.setValue('operatorLabel', op.name);
                            }}
                            className={cn(
                              'flex flex-col items-center gap-2 rounded-xl border p-4 w-36 cursor-pointer transition-all',
                              field.value === op.id
                                ? 'border-emerald-500 bg-emerald-50'
                                : 'border-slate-200 hover:border-emerald-200 hover:shadow-md hover:-translate-y-0.5'
                            )}
                          >
                            <img
                              src={
                                operatorImages[op.name] ||
                                '/images/operators/default.svg'
                              }
                              alt={op.name}
                              className="w-12 h-12 object-contain"
                            />
                            <span className="text-sm font-medium text-slate-900">
                              {op.name}
                            </span>
                          </button>
                        ))}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </section>

            {/* Quantity Selection */}
            <section className="space-y-4">
              <h3 className="text-lg font-bold text-slate-900">
                Quantité
              </h3>
              <p className="text-sm text-slate-500">
                Nombre de cartes SIM à commander (minimum 5)
              </p>

              <FormField
                control={form.control}
                name="quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-slate-700">
                      Quantité
                    </FormLabel>
                    <FormControl>
                      <QuantitySelector
                        value={field.value}
                        onChange={(value) => field.onChange(value)}
                        min={5}
                      />
                    </FormControl>
                    <FormMessage />
                    {quantity >= 5 && (
                      <p className="text-sm text-slate-500 mt-2">
                        Total: {(quantity * 3).toFixed(2)}€ ({quantity} ×
                        3€)
                      </p>
                    )}
                  </FormItem>
                )}
              />
            </section>

            {/* Submit Button */}
            <div className="flex items-center justify-end pt-6 border-t border-slate-200">
              <PermissionGuard permission="write">
                <Button
                  type="button"
                  disabled={
                    form.formState.isSubmitting ||
                    !form.formState.isValid ||
                    !siteId ||
                    !operatorId ||
                    quantity < 5 ||
                    isPending
                  }
                  onClick={handleAddSimsToCheckout}
                  className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-4 shadow-sm hover:shadow-md transition-all"
                >
                  <ShoppingCart className="h-3.5 w-3.5" />
                  Ajouter au panier
                </Button>
              </PermissionGuard>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
